import { WikiLayout } from "@/components/WikiLayout";
import { WikiPage } from "@/components/WikiPage";

const AppDocumentation = () => {
  return (
    <WikiLayout>
      <WikiPage 
        title="App Documentation Guide" 
        description="How to properly document service visits for payment eligibility"
      >
        <section className="space-y-8">
          <div className="bg-card border-2 border-destructive rounded-lg p-6">
            <p className="text-xl font-bold text-destructive mb-2">
              If it's not logged, it didn't happen.
            </p>
            <p className="text-muted-foreground">
              Payment eligibility requires complete and accurate documentation in the BlueLux Technician App.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Payment Requirements</h2>
            <p className="text-muted-foreground mb-4">
              Every service visit must include:
            </p>
            <ul className="list-disc list-inside space-y-2 text-muted-foreground">
              <li>Job completion marked in the App</li>
              <li>Full checklist completion</li>
              <li>Water chemistry readings and dosages entered</li>
              <li>Before & after photos uploaded</li>
            </ul>
            <p className="text-sm text-muted-foreground mt-4 italic">
              Jobs not fully logged are not eligible for payment.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Required Photos for Every Visit</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium">Before Photos</h3>
                <ul className="list-disc list-inside text-muted-foreground space-y-1 mt-2">
                  <li>Pool/spa overall condition</li>
                  <li>Skimmer baskets (before cleaning)</li>
                  <li>Pump basket (before cleaning)</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-medium">During Service</h3>
                <ul className="list-disc list-inside text-muted-foreground space-y-1 mt-2">
                  <li>Test strip with readings clearly visible</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-medium">After Photos</h3>
                <ul className="list-disc list-inside text-muted-foreground space-y-1 mt-2">
                  <li>Pool/spa overall condition (cleaned)</li>
                  <li>Skimmer baskets (after cleaning)</li>
                  <li>Pump basket (after cleaning)</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-medium">As Needed</h3>
                <ul className="list-disc list-inside text-muted-foreground space-y-1 mt-2">
                  <li>Any algae issues</li>
                  <li>Equipment concerns or malfunctions</li>
                  <li>Customer requests or special conditions</li>
                  <li>Safety hazards</li>
                </ul>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Water Chemistry Logging</h2>
            <p className="text-muted-foreground mb-4">
              Record all water chemistry readings in the App:
            </p>
            <ul className="list-disc list-inside space-y-2 text-muted-foreground">
              <li>pH level</li>
              <li>Free chlorine</li>
              <li>Total alkalinity</li>
              <li>Stabilizer (cyanuric acid)</li>
              <li>Salt level (if applicable)</li>
              <li>Any other relevant readings</li>
            </ul>
          </div>

          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Chemical Addition Logging</h2>
            <p className="text-muted-foreground mb-4">
              Record every chemical addition made during the visit:
            </p>
            <ul className="list-disc list-inside space-y-2 text-muted-foreground">
              <li>Chemical name</li>
              <li>Amount added (exact quantity)</li>
              <li>Reason for addition</li>
            </ul>
            <p className="text-sm text-muted-foreground mt-4 italic">
              All chemicals are provided by BlueLux and must be logged accurately to maintain inventory and quality control.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Service Checklist</h2>
            <p className="text-muted-foreground mb-4">
              Complete all checklist items in the App for each visit:
            </p>
            <ol className="list-decimal list-inside space-y-2 text-muted-foreground">
              <li>Start route on time</li>
              <li>Skim surface debris</li>
              <li>Brush walls, steps, and tile line</li>
              <li>Empty skimmer and pump baskets</li>
              <li>Test water chemistry</li>
              <li>Balance chemicals as needed</li>
              <li>Inspect all equipment</li>
              <li>Verify pool is safe for use</li>
              <li>Upload all required photos</li>
              <li>Mark job complete</li>
            </ol>
          </div>

          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Additional Service Event Documentation</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium">Filter Cleans</h3>
                <ul className="list-disc list-inside text-muted-foreground space-y-1 mt-2">
                  <li>PSI reading before service</li>
                  <li>Photos of filter cartridges/grids before cleaning</li>
                  <li>Photos of filter cartridges/grids after cleaning</li>
                  <li>Reset PSI reading after reassembly</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-medium">Salt Cell Cleans</h3>
                <ul className="list-disc list-inside text-muted-foreground space-y-1 mt-2">
                  <li>Photos of cell plates before cleaning</li>
                  <li>Photos of cell plates after cleaning</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-medium">Repairs/Installs</h3>
                <ul className="list-disc list-inside text-muted-foreground space-y-1 mt-2">
                  <li>Clear photos of parts being replaced</li>
                  <li>Photos of work being performed</li>
                  <li>Photos of equipment in working order after repair</li>
                  <li>Detailed notes on parts used and work completed</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-muted/50 border border-border rounded-lg p-6 mt-8">
            <h3 className="text-lg font-medium mb-3">Best Practices for App Documentation</h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>✓ Take photos in good lighting with clear focus</li>
              <li>✓ Include test strips in frame with readings visible</li>
              <li>✓ Document unusual conditions or concerns immediately</li>
              <li>✓ Double-check all entries before marking job complete</li>
              <li>✓ Log chemical additions immediately after application</li>
              <li>✓ Report equipment issues with detailed photos and notes</li>
            </ul>
          </div>
        </section>
      </WikiPage>
    </WikiLayout>
  );
};

export default AppDocumentation;
